import "./App.css";
import React, { useRef, useEffect, useState } from "react";
import Header from "./components/Header";
import {
  AuthenticatedTemplate,
  useIsAuthenticated,
  useMsalAuthentication,
} from "@azure/msal-react";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import { MuiColorInput } from "mui-color-input";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import { Modal, TextField } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { InteractionType } from "@azure/msal-browser";
import MapDataComponent from "./components/MapDataComponent";
import MapContainer from "./components/MapContainer";
import MapsterAlert from "./components/MapsterAlert";
import errorService from "./services/errorService";
import { throttle } from 'lodash';
import {
  ALLOWED_HOSTS,
  DATA_SOURCES,
  ENDPOINT_DATA_TYPES,
  MAP_LAYER_CONFIG,
  SERVERS,
  APP_ENV,
  API_SERVER,
  DEFAULT_VALUES,
  LAYER_TEMPLET,
  ALERT_MESSAGE,
} from "./config";

let tile_api_server;
if (APP_ENV.LOCAL.includes(process.env.REACT_APP_RUN_ENV)) {
  tile_api_server = API_SERVER.LOCAL;
} else if (process.env.REACT_APP_RUN_ENV === APP_ENV.DEV) {
  tile_api_server = API_SERVER.DEV;
} else {
  tile_api_server = API_SERVER.PROD;
}

console.log("Tile API Server: " + tile_api_server);

// //TODO: Refresh this (get another token) if session goes over Token's TTL (time to live)
// async function getBearerToken() {
//   const res = await fetch("http://localhost:5006/api/token");
//   const data = await res.json();
//   return data.token;
//}

const App = (bearer_token) => {
  const isAuthenticated = useIsAuthenticated();
  const { login, result, error } = useMsalAuthentication(
    InteractionType.Redirect
  );
  const mapPopups = new Map();
  const mapContainer = useRef(null);
  const prevLayersRef = useRef([]);
  const map = useRef(null);
  const [layers, setLayers] = useState({
    "layer-1": {
      ...LAYER_TEMPLET,
      id: "layer-1",
      name: "Default Layer",
      color: "#0000FF",
      dataEndpoint: ENDPOINT_DATA_TYPES.UNSTRUCTURED,
      dataSource: DATA_SOURCES.S3,
      serverInput: SERVERS.S3_SERVER,
      s3Account: DEFAULT_VALUES.s3DefaultAccount,
      s3Bucket: DEFAULT_VALUES.s3DefaultBucket,
      s3FilePath: DEFAULT_VALUES.unstucturedS3DefaultFilePath,
      geometryCol: DEFAULT_VALUES.defaultGeometryCol,
      isNewLayer: false,
    },
  });
  const [lng, setLng] = useState(-83.605);
  const [lat, setLat] = useState(42.601);
  const [zoom, setZoom] = useState(14);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [newLayerName, setNewLayerName] = useState("");
  const [newLayerColor, setNewLayerColor] = useState("#0000FF");
  const [editingLayerId, setEditingLayerId] = useState(null);
  const [loadingSpinning, setLoadingSpinning] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [duplicateNameError, setDuplicateNameError] = useState(false);
  const [accordionExpanded, setAccordionExpanded] = useState(
    Object.keys(layers)[0]
  );

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setAccordionExpanded(isExpanded ? panel : false);
  };

  const handleSourceDataLoading = (layerKey) => (event) => {
    if (event.sourceId === layerKey) {
      console.log("Data is loading");
      setLoadingSpinning(true);
    }
  };

  const handleSourceData = (layerKey) => (event) => {
    if (event.sourceId === layerKey) {
      console.log("Data is loaded");
      setLoadingSpinning(false);
    }
  };

  const handleError = (layerKey) => async (event) => {
    if (event.sourceId === layerKey) {
      setLoadingSpinning(false);
    }
    if (
      event.error &&
      ((event.error.status >= 500 && event.error.status < 600) ||
        event.error.status === 400 ||
        event.error.status === 422)
    ) {
      let errorMessage = ALERT_MESSAGE.STATUS_MAP[event.error.status];
      if (!errorMessage) {
        errorMessage = ALERT_MESSAGE.UNKNOW_ERROR_STATUS;
      }
      try {
        const response = await fetch(event.error.url, {
          headers: {
            Authorization: `Bearer ${bearer_token.bearer_token}`,
          },
        });

        if (response.status === 208) {
          let responseText = await response.text();
          responseText = responseText.replace(/^"|"$/g, '');
          event.error.message = errorMessage;
          if (responseText) {
            event.error.detail = responseText;
          } else {
            event.error.detail = ALERT_MESSAGE.DEFAULT_DETAIL;
          }
        }
      } catch (error) {}
      errorService.handleError(event.error);
      event.error.detail = ALERT_MESSAGE.DEFAULT_DETAIL;
    }
  };

  const handleMouseMove = (layerKey) => (e) => {
    console.log("Mouse move event: ", layerKey);
    if (map.current && map.current.getCanvas) {
      map.current.getCanvas().style.cursor = "pointer";
      const coordinates = e.features[0].geometry.coordinates.slice();
      const description = Object.keys(e.features[0].properties)
        .map((key) => `${key}: ${e.features[0].properties[key]}`)
        .join("<br>");
      let popup = mapPopups.get(layerKey);
      if (!popup) {
        popup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
        });
        mapPopups.set(layerKey, popup);
      }
      popup.setLngLat(coordinates).setHTML(description).addTo(map.current);
    }
  };

  const handleMouseLeave = (layerKey) => () => {
    console.log("Mouse move event: ", layerKey);
    if (map.current && map.current.getCanvas) {
      map.current.getCanvas().style.cursor = "";
      const popup = mapPopups.get(layerKey);
      if (popup) {
        popup.remove();
      }
    }
  };

  const addEventsListenersToLayer = (layerId) => {
    map.current.on("sourcedataloading", handleSourceDataLoading(layerId));
    map.current.on("sourcedata", handleSourceData(layerId));
    map.current.on("error", handleError(layerId));
    map.current.on("mousemove", layerId, handleMouseMove(layerId));
    map.current.on("mouseleave", layerId, handleMouseLeave(layerId));
  };

  useEffect(() => {
    if (!isAuthenticated) return; // don't try to initialize map if element is not rendered (not authenticated)
    if (map.current) return; // initialize map only once

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      accessToken: process.env.REACT_APP_MAPBOX_KEY,
      style: "mapbox://styles/mapbox/satellite-streets-v12",
      center: [lng, lat],
      zoom: zoom,
      transformRequest: (url, resourceType) => {
        let url_proper = new URL(url);
        let host = url_proper.hostname;
        let allowedHosts = ALLOWED_HOSTS;
        if (resourceType === "Tile" && allowedHosts.includes(host)) {
          return {
            url: url,
            headers: {
              Authorization: `Bearer ${bearer_token.bearer_token}`,
            },
            credentials: "include",
          };
        }
      },
    });

    map.current.on("load", () => {
      Object.keys(layers).forEach((layerKey) => {
        console.log("Adding layer to map: " + layerKey);
        addLayerToMap(layers[layerKey]);
      });
      prevLayersRef.current = layers;
    });

    if (map.current && layers) {
      console.log("Adding event listeners to new layers");
      const prevLayers = prevLayersRef.current;
      const currentLayerKeys = Object.keys(layers);
      const prevLayerKeys = Object.keys(prevLayers);
      const newLayerKeys = currentLayerKeys.filter(
        (key) => !prevLayerKeys.includes(key)
      );
      if (newLayerKeys.length !== 0) {
        newLayerKeys.forEach((layerId) => {
          addEventsListenersToLayer(layerId);
        });
        prevLayersRef.current = layers;
      }
    }

    const throttledRepaint = throttle(() => {
      map.current.triggerRepaint();
    }, 50); // Note: this will only allow repaint every 50ms regardless of how many times the event is fired

    map.current.on("move", () => {
      setLng(map.current.getCenter().lng.toFixed(4));
      setLat(map.current.getCenter().lat.toFixed(4));
      setZoom(map.current.getZoom().toFixed(2));
      throttledRepaint();
    });
  });

  const addLayerToMap = (layer) => {
    const sourceAttrs = {
      type: "vector",
      tiles: [],
      minzoom: 4,
      maxzoom: 20,
    };
    let map_paint = MAP_LAYER_CONFIG.PAINT;
    map_paint["circle-color"] = layer.color;
    const layer_config = {
      id: layer.id,
      type: MAP_LAYER_CONFIG.TYPE,
      source: layer.id,
      paint: map_paint,
    };

    let layerAttrs;
    if (layer.dataEndpoint === ENDPOINT_DATA_TYPES.UNSTRUCTURED) {
      if (layer.dataSource === DATA_SOURCES.S3) {
        layerAttrs = {
          id: layer.id,
          type: "fill",
          "source-layer": layer.s3FilePath.split("/").pop(),
          source: layer.id,
          ...layer_config,
        };
        sourceAttrs.tiles = [
          `${tile_api_server}/v1/map/{z}/{x}/{y}.pbf?s3_server=${
            layer.serverInput
          }&s3_account=${layer.s3Account}&s3_bucket=${
            layer.s3Bucket
          }&s3_filepath=${layer.s3FilePath.replaceAll(
            "/",
            "%2F"
          )}&geometry_column=${layer.geometryCol}`,
        ];
      } else if (layer.dataSource === DATA_SOURCES.ADLS) {
        layerAttrs = {
          id: layer.id,
          type: "fill",
          "source-layer": layer.adlsFilepath.split("/").pop(),
          source: layer.id,
          ...layer_config,
        };
        sourceAttrs.tiles = [
          `${tile_api_server}/v1/map/{z}/{x}/{y}.pbf?adls_server=${
            layer.serverInput
          }&adls_container=${
            layer.adlsContainer
          }&adls_filepath=${layer.adlsFilepath.replaceAll(
            "/",
            "%2F"
          )}&geometry_column=${layer.geometryCol}`,
        ];
      } else {
        layerAttrs = {
          id: layer.id,
          type: "fill",
          "source-layer": layer.unityCatalogTable,
          source: layer.id,
          ...layer_config,
        };
        sourceAttrs.tiles = [
          `${tile_api_server}/v1/map/{z}/{x}/{y}.pbf?unity_catalog_server=${layer.serverInput}&unity_catalog_catalog=${layer.unityCatalogCatalog}&unity_catalog_schema=${layer.unityCatalogSchema}&unity_catalog_table=${layer.unityCatalogTable}&geometry_column=${layer.geometryCol}&unity_catalog_differentiator_column=${layer.unityCatalogDiffCol}&unity_catalog_differentiator_value=${layer.unityCatalogDiffVal}`,
        ];
      }
    } else {
      layerAttrs = {
        id: layer.id,
        type: "fill",
        "source-layer": `${layer.feature}_${layer.layer}`,
        source: layer.id,
        ...layer_config,
      };
      sourceAttrs.tiles = [
        `${tile_api_server}/v1/map/usms/{z}/{x}/{y}.pbf?s3_server=${
          layer.serverInput
        }&s3_account=${layer.s3Account}&s3_bucket=${
          layer.s3Bucket
        }&s3_filepath=${layer.usmsFilePath.replaceAll("/", "%2F")}&feature=${
          layer.usmsFeature
        }&layer=${layer.usmsLayer}&geometry_column=${layer.geometryCol}`,
      ];
    }

    map.current.addSource(layer.id, sourceAttrs);
    map.current.addLayer(layerAttrs);
    map.current.triggerRepaint();
  };

  const handleUpdateButtonClick = (layer) => {
    if (!map.current) {
      console.error("Map instance is not initialized.");
      return;
    }

    let map_paint = MAP_LAYER_CONFIG.PAINT;
    map_paint["circle-color"] = layer.color;
    const layer_config = {
      id: layer.id,
      type: MAP_LAYER_CONFIG.TYPE,
      source: layer.id,
      paint: map_paint,
    };

    if (map.current.getLayer(layer.id)) {
      map.current.removeLayer(layer.id);
    }
    if (map.current.getSource(layer.id)) {
      map.current.removeSource(layer.id);
    }

    const sourceAttrs = {
      type: "vector",
      tiles: [],
      minzoom: 4,
      maxzoom: 20,
    };

    let layerAttrs;
    if (layer.dataEndpoint === ENDPOINT_DATA_TYPES.UNSTRUCTURED) {
      if (layer.dataSource === DATA_SOURCES.S3) {
        layerAttrs = {
          id: layer.id,
          type: "fill",
          "source-layer": layer.s3FilePath.split("/").pop(),
          source: layer.id,
          ...layer_config,
        };
        sourceAttrs.tiles = [
          `${tile_api_server}/v1/map/{z}/{x}/{y}.pbf` +
            `?s3_server=${layer.serverInput}` +
            `&s3_account=${layer.s3Account}` +
            `&s3_bucket=${layer.s3Bucket}` +
            `&s3_filepath=${layer.s3FilePath.replaceAll("/", "%2F")}` +
            `&geometry_column=${layer.geometryCol}`,
        ];
      } else if (layer.dataSource === DATA_SOURCES.ADLS) {
        layerAttrs = {
          id: layer.id,
          type: "fill",
          "source-layer": layer.adlsFilepath.split("/").pop(),
          source: layer.id,
          ...layer_config,
        };
        sourceAttrs.tiles = [
          `${tile_api_server}/v1/map/{z}/{x}/{y}.pbf` +
            `?adls_server=${layer.serverInput}` +
            `&adls_container=${layer.adlsContainer}` +
            `&adls_filepath=${layer.adlsFilepath.replaceAll("/", "%2F")}` +
            `&geometry_column=${layer.geometryCol}`,
        ];
      } else if (layer.dataSource === DATA_SOURCES.UNITY_CATALOG) {
        layerAttrs = {
          id: layer.id,
          type: "fill",
          "source-layer": layer.unityCatalogTable,
          source: layer.id,
          ...layer_config,
        };
        sourceAttrs.tiles = [
          `${tile_api_server}/v1/map/{z}/{x}/{y}.pbf` +
            `?unity_catalog_server=${layer.serverInput}` +
            `&unity_catalog_catalog=${layer.unityCatalogCatalog}` +
            `&unity_catalog_schema=${layer.unityCatalogSchema}` +
            `&unity_catalog_table=${layer.unityCatalogTable}` +
            `&geometry_column=${layer.geometryCol}` +
            `&unity_catalog_differentiator_column=${layer.unityCatalogDiffCol}` +
            `&unity_catalog_differentiator_value=${layer.unityCatalogDiffVal}`,
        ];
      }
    } else {
      layerAttrs = {
        id: layer.id,
        type: "fill",
        "source-layer": `${layer.usmsFeature}_${layer.usmsLayer}`,
        source: layer.id,
        ...layer_config,
      };
      sourceAttrs.tiles = [
        `${tile_api_server}/v1/map/usms/{z}/{x}/{y}.pbf` +
          `?s3_server=${layer.serverInput}` +
          `&s3_account=${layer.s3Account}` +
          `&s3_bucket=${layer.s3Bucket}` +
          `&s3_filepath=${layer.usmsFilePath.replaceAll("/", "%2F")}` +
          `&feature=${layer.usmsFeature}` +
          `&layer=${layer.usmsLayer}` +
          `&geometry_column=${layer.geometryCol}`,
      ];
    }
    map.current.addSource(layer.id, sourceAttrs);
    map.current.addLayer(layerAttrs);
    map.current.triggerRepaint();
  };

  const handleAddLayer = () => {
    setIsUserModalOpen(true);
  };

  const addFirstLayer = () => {
    const layerId = "layer-1";
    if (newLayerName === "") {
      setNameError(true);
      setDuplicateNameError(false);
      return;
    }
    const newLayer = {
      ...LAYER_TEMPLET,
      id: layerId,
      name: newLayerName,
      color: newLayerColor,
      dataEndpoint: ENDPOINT_DATA_TYPES.UNSTRUCTURED,
      dataSource: DATA_SOURCES.S3,
      serverInput: SERVERS.S3_SERVER,
      s3Account: DEFAULT_VALUES.s3DefaultAccount,
      s3Bucket: DEFAULT_VALUES.s3DefaultBucket,
      s3FilePath: DEFAULT_VALUES.unstucturedS3DefaultFilePath,
      geometryCol: DEFAULT_VALUES.defaultGeometryCol,
      isNewLayer: true,
    };
    setLayers({ [layerId]: newLayer });
    setIsUserModalOpen(false);
    setNewLayerName("");
    setNewLayerColor("#0000FF");
    setEditingLayerId(null);
    setNameError(false);
    setDuplicateNameError(false);
    setAccordionExpanded(layerId);
  };

  const handleConfirmAddLayer = () => {
    if (Object.keys(layers).length === 0) {
      console.log("No layers to copy from.");
      addFirstLayer();
      return;
    }
    if (newLayerName === "") {
      setNameError(true);
      setDuplicateNameError(false);
      return;
    }
    let isDuplicate = false;
    Object.keys(layers).forEach((layerId_) => {
      if (newLayerName === layers[layerId_].name) {
        isDuplicate = true;
      }
    });

    if (isDuplicate) {
      setDuplicateNameError(true);
      setNameError(false);
      return;
    }
    const lastLayerKey = Object.keys(layers).pop();
    const lastLayer = layers[lastLayerKey];

    const newLayerId = `layer-${Object.keys(layers).length + 1}`;
    const newLayer = {
      ...lastLayer,
      id: newLayerId,
      name: newLayerName,
      color: newLayerColor,
      isNewLayer: true,
    };

    setLayers((prevLayers) => ({
      ...prevLayers,
      [newLayerId]: newLayer,
    }));
    addEventsListenersToLayer(newLayerId);
    setIsUserModalOpen(false);
    setNewLayerName("");
    setNewLayerColor("#0000FF");
    setEditingLayerId(null);
    setNameError(false);
    setDuplicateNameError(false);
    setAccordionExpanded(newLayerId);
  };

  const handleCancelAddLayer = () => {
    setIsUserModalOpen(false);
    setNewLayerName("");
    setNewLayerColor("#0000FF");
    setEditingLayerId(null);
    setNameError(false);
    setDuplicateNameError(false);
  };

  const handleDeleteLayer = (layerId) => {
    if (!map.current) {
      console.error("Map instance is not initialized.");
      return;
    }
    if (map.current.getLayer(layerId)) {
      map.current.removeLayer(layerId);
    }
    if (map.current.getSource(layerId)) {
      map.current.removeSource(layerId);
    }
    const newLayers = { ...layers };
    delete newLayers[layerId];
    setLayers(newLayers);
  };

  const handleColorChange = (color) => {
    setNewLayerColor(color);
  };

  const handleConfirmEditLayer = (layerId) => {
    if (newLayerName === '') {
      setNameError(true);
      setDuplicateNameError(false);
      return;
    }

    let isDuplicate = false;
    Object.keys(layers).forEach((layerId_) => {
      if (newLayerName === layers[layerId_].name && layerId !== layerId_) {
        isDuplicate = true;
      }
    });

    if (isDuplicate) {
      setDuplicateNameError(true);
      setNameError(false);
      return;
    }

    const layerToUpdate = layers[layerId];
    const updatedLayer = {
      ...layerToUpdate,
      name: newLayerName,
      color: newLayerColor,
    };
    setLayers((prevLayers) => ({
      ...prevLayers,
      [layerId]: updatedLayer,
    }));
    setIsUserModalOpen(false);
    setNewLayerName("");
    setNewLayerColor("#0000FF");
    setEditingLayerId(null);
    setNameError(false);
    setDuplicateNameError(false);
  };

  const handleToggleLayerVisibility = (layerId, event) => {
    let visibility;
    const layerName = layers[layerId].name;
    if (event.target.checked) {
      visibility = "visible";
    } else {
      visibility = "none";
    }
    map.current.setLayoutProperty(layerId, "visibility", visibility);
  };

  return (
    <div>
      <AuthenticatedTemplate>
        <div>
          <Stack spacing={1}>
            <Header />
            <MapsterAlert
              title={ALERT_MESSAGE.ERROR.TITLE}
              severity={ALERT_MESSAGE.ERROR.SEVERITY}
            />
            <div>
              <Grid container spacing={1}>
                <Grid item xs={4} xl={3}>
                  <Stack spacing={5}>
                    <div>
                      <Button onClick={handleAddLayer}>Add Layer</Button>
                      <Modal
                        open={isUserModalOpen}
                        onClose={handleCancelAddLayer}
                      >
                        <div
                          style={{
                            padding: "20px",
                            backgroundColor: "white",
                            margin: "auto",
                            marginTop: "10%",
                            width: "300px",
                            borderRadius: "8px",
                          }}
                        >
                          <h2>
                            {editingLayerId ? "Edit Layer" : "Add New Layer"}
                          </h2>
                          <TextField
                            label="Layer Name"
                            value={newLayerName}
                            onChange={(e) => setNewLayerName(e.target.value)}
                            fullWidth
                            margin="normal"
                            error={nameError || duplicateNameError}
                            inputProps={{ maxLength: 50 }}
                            helperText={
                              nameError
                                ? "Layer Name is required"
                                : duplicateNameError
                                ? "Layer Name already exists"
                                : ""
                            }
                          />
                          <MuiColorInput
                            format="hex"
                            value={newLayerColor}
                            onChange={handleColorChange}
                            isAlphaHidden={true}
                          />
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginTop: "20px",
                            }}
                          >
                            <Button onClick={handleCancelAddLayer}>
                              Cancel
                            </Button>
                            <Button
                              onClick={
                                editingLayerId
                                  ? () => handleConfirmEditLayer(editingLayerId)
                                  : handleConfirmAddLayer
                              }
                              variant="contained"
                              color="primary"
                            >
                              Confirm
                            </Button>
                          </div>
                        </div>
                      </Modal>
                      {Object.entries(layers).map(([layerId, layer]) => (
                        <Accordion
                          expanded={accordionExpanded === layerId}
                          onChange={handleAccordionChange(layerId)}
                          key={layerId}
                          className="accordion"
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel1a-${layerId}-content`}
                            id={`panel1a-${layerId}-header`}
                            onClick={(event) => {
                              if (
                                event.target.tagName === "BUTTON" ||
                                event.target.tagName === "INPUT"
                              ) {
                                event.stopPropagation();
                              }
                            }}
                            className="accordion-summary"
                          >
                            <Typography className="layer-name">
                              <Tooltip title={layer.name}>
                                <span className="layer-name-text">
                                  {layer.name.length > 25
                                    ? `${layer.name.substring(0, 20)}...`
                                    : layer.name}
                                </span>
                              </Tooltip>
                            </Typography>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginLeft: "auto",
                              }}
                            >
                              <Button
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleDeleteLayer(layer.id);
                                }}
                              >
                                Remove
                              </Button>
                              <Button
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setIsUserModalOpen(true);
                                  setNewLayerName(layer.name);
                                  setNewLayerColor(layer.color);
                                  setEditingLayerId(layerId);
                                }}
                              >
                                Edit
                              </Button>
                              <Switch
                                defaultChecked
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleToggleLayerVisibility(layer.id, event);
                                }}
                              />
                              <div
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  backgroundColor: layer.color,
                                  marginLeft: "10px",
                                  borderRadius: "50%",
                                }}
                              />
                            </div>
                          </AccordionSummary>
                          <AccordionDetails>
                            <MapDataComponent
                              key={layer.id}
                              layers={layers}
                              layerId={layer.id}
                              setLayers={setLayers}
                              handleUpdateButtonClick={handleUpdateButtonClick}
                            />
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </div>
                  </Stack>
                </Grid>
                <Grid item xs={8} xl={9}>
                  <MapContainer
                    mapContainer={mapContainer}
                    lng={lng}
                    lat={lat}
                    zoom={zoom}
                    loadingSpinning={loadingSpinning}
                  />
                </Grid>
              </Grid>
            </div>
          </Stack>
        </div>
      </AuthenticatedTemplate>
    </div>
  );
};

export default App;
